import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import ChartDirectorChart from './ChartDirectorChart';

const money = (value, showSign = false) => {
  const number = Number(value || 0);
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: showSign ? 'always' : 'auto',
  });
};

const number = (value, digits = 2) => {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const date = (value) => value || '—';

// Extends the chart 1 week past the trade's exit so you can see whether a
// wider stop would have helped — but never past today, since that data
// doesn't exist yet.
const oneWeekAfter = (value) => {
  if (!value) return value;
  const base = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(base.getTime())) return value;
  base.setUTCDate(base.getUTCDate() + 7);
  const today = new Date().toISOString().slice(0, 10);
  const extended = base.toISOString().slice(0, 10);
  return extended > today ? today : extended;
};

const loggedAt = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const SORT_VALUE = {
  logged: (trade) => trade.created_at ? new Date(trade.created_at).getTime() : null,
  symbol: (trade) => trade.symbol,
  status: (trade) => trade.status,
  type: (trade) => trade.order_type,
  quantity: (trade) => Number(trade.quantity),
  entryDate: (trade) => trade.entry_date,
  entry: (trade) => Number(trade.entry_price),
  currentDate: (trade) => trade.status === 'open' ? trade.current_date : trade.exit_date,
  current: (trade) => Number(trade.current_price),
  stop: (trade) => Number(trade.stop_price),
  reason: (trade) => trade.exit_reason,
  pnl: (trade) => Number(trade.pnl),
  return: (trade) => Number(trade.return_pct),
};

const SortableTradeHeader = ({ label, sortKey, sort, onSort, numberColumn = false }) => {
  const active = sort.key === sortKey;
  return <th
    className={`${numberColumn ? 'number ' : ''}sortable-trade-header`}
    aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
  >
    <button type="button" onClick={() => onSort(sortKey)}>
      {label}{active ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  </th>;
};

const TradesPage = ({ account, username, scrollTopRef }) => {
  const trades = account?.trades || [];
  const tradesScrollRef = useRef(null);
  const [expandedTradeId, setExpandedTradeId] = useState(null);
  const [sort, setSort] = useState({ key: 'logged', direction: 'desc' });

  const sortedTrades = useMemo(() => {
    const valueFor = SORT_VALUE[sort.key];
    return [...trades].sort((a, b) => {
      const aValue = valueFor(a);
      const bValue = valueFor(b);
      const aMissing = aValue == null || aValue === '' || (typeof aValue === 'number' && !Number.isFinite(aValue));
      const bMissing = bValue == null || bValue === '' || (typeof bValue === 'number' && !Number.isFinite(bValue));
      if (aMissing && bMissing) return String(a.symbol).localeCompare(String(b.symbol));
      if (aMissing) return 1;
      if (bMissing) return -1;
      const comparison = typeof aValue === 'number'
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue));
      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [trades, sort]);

  const changeSort = (key) => setSort((current) => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));

  useEffect(() => {
    if (tradesScrollRef.current) {
      tradesScrollRef.current.scrollTop = scrollTopRef.current || 0;
    }
  }, [scrollTopRef]);

  return (
    <main className="trades-page">
      <section className="account-summary" aria-label={`${username} account summary`}>
        <div><span>Starting capital</span><strong>{money(account.starting_capital)}</strong></div>
        <div><span>Running amount</span><strong>{money(account.running_amount)}</strong></div>
        <div><span>Realised P/L</span><strong className={account.realised_pnl >= 0 ? 'positive' : 'negative'}>{money(account.realised_pnl, true)}</strong></div>
        <div><span>Unrealised P/L</span><strong className={account.unrealised_pnl >= 0 ? 'positive' : 'negative'}>{money(account.unrealised_pnl, true)}</strong></div>
        <div><span>Closed trades</span><strong>{account.closed_trades}</strong></div>
        <div><span>Win rate</span><strong>{number(account.win_rate, 1)}%</strong></div>
      </section>

      <section className="trades-card">
        <div className="trades-heading">
          <strong>{username}'s trades</strong>
          <span>{trades.length} taken</span>
        </div>
        {trades.length === 0 ? (
          <div className="trades-empty">No filled trades yet</div>
        ) : (
          <div
            className="trades-scroll"
            ref={tradesScrollRef}
            onScroll={(event) => { scrollTopRef.current = event.currentTarget.scrollTop; }}
          >
            <table className="trades-table">
              <thead>
                <tr>
                  <SortableTradeHeader label="Logged" sortKey="logged" sort={sort} onSort={changeSort} />
                  <SortableTradeHeader label="Symbol" sortKey="symbol" sort={sort} onSort={changeSort} />
                  <SortableTradeHeader label="P/L" sortKey="pnl" sort={sort} onSort={changeSort} numberColumn />
                  <SortableTradeHeader label="Return" sortKey="return" sort={sort} onSort={changeSort} numberColumn />
                  <SortableTradeHeader label="Status" sortKey="status" sort={sort} onSort={changeSort} />
                  <SortableTradeHeader label="Type" sortKey="type" sort={sort} onSort={changeSort} />
                  <SortableTradeHeader label="Qty" sortKey="quantity" sort={sort} onSort={changeSort} numberColumn />
                  <SortableTradeHeader label="Entry date" sortKey="entryDate" sort={sort} onSort={changeSort} />
                  <SortableTradeHeader label="Entry" sortKey="entry" sort={sort} onSort={changeSort} numberColumn />
                  <SortableTradeHeader label={trades.some((trade) => trade.status === 'open') ? 'Exit / Current date' : 'Exit date'} sortKey="currentDate" sort={sort} onSort={changeSort} />
                  <SortableTradeHeader label="Exit / Current" sortKey="current" sort={sort} onSort={changeSort} numberColumn />
                  <SortableTradeHeader label="Stop" sortKey="stop" sort={sort} onSort={changeSort} numberColumn />
                  <SortableTradeHeader label="Reason" sortKey="reason" sort={sort} onSort={changeSort} />
                </tr>
              </thead>
              <tbody>
                {sortedTrades.map((trade) => {
                  const completed = trade.status === 'closed';
                  const expanded = completed && String(expandedTradeId) === String(trade.id);
                  const toggleExpanded = () => setExpandedTradeId((current) => (
                    String(current) === String(trade.id) ? null : trade.id
                  ));
                  return (
                  <Fragment key={`${trade.session_id}-${trade.id}`}>
                  <tr
                    className={completed ? 'completed-trade-row' : ''}
                    onClick={completed ? toggleExpanded : undefined}
                    onKeyDown={completed ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleExpanded();
                      }
                    } : undefined}
                    role={completed ? 'button' : undefined}
                    tabIndex={completed ? 0 : undefined}
                    aria-expanded={completed ? expanded : undefined}
                  >
                    <td>{loggedAt(trade.created_at)}</td>
                    <td className="symbol">{trade.symbol}<small>{trade.market}</small></td>
                    <td className={`number ${trade.pnl >= 0 ? 'positive' : 'negative'}`}>{money(trade.pnl, true)}</td>
                    <td className={`number ${trade.return_pct >= 0 ? 'positive' : 'negative'}`}>{number(trade.return_pct)}%</td>
                    <td><span className={`status-pill ${trade.status}`}>{trade.status}</span></td>
                    <td className="capitalise">{trade.order_type}</td>
                    <td className="number">{number(trade.quantity, 0)}</td>
                    <td>{date(trade.entry_date)}</td>
                    <td className="number">{number(trade.entry_price, 4)}</td>
                    <td>{date(trade.status === 'open' ? trade.current_date : trade.exit_date)}</td>
                    <td className="number">{number(trade.current_price, 4)}</td>
                    <td className="number">{number(trade.stop_price, 4)}</td>
                    <td className="capitalise">{trade.status === 'open' ? '—' : (trade.exit_reason || '—').replaceAll('_', ' ')}</td>
                  </tr>
                  {expanded && (
                    <tr className="completed-trade-chart-row">
                      <td colSpan="13">
                        <div className="completed-trade-chart-heading">
                          <strong>{trade.symbol}</strong>
                          <span>Entry {date(trade.entry_date)} at {number(trade.entry_price, 4)}</span>
                          <span>Exit {date(trade.exit_date)} at {number(trade.exit_price ?? trade.current_price, 4)}</span>
                        </div>
                        <div className="completed-trade-chart">
                          <ChartDirectorChart
                            market={trade.market || 'US'}
                            symbol={trade.symbol}
                            date={oneWeekAfter(trade.exit_date || trade.current_date) || new Date().toISOString().slice(0, 10)}
                            period="daily"
                            transactionMarkers={[
                              { date: trade.entry_date, price: trade.entry_price, kind: 'entry', label: 'ENTRY' },
                              { date: trade.exit_date, price: trade.exit_price ?? trade.current_price, kind: 'exit', label: 'EXIT' },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default TradesPage;
